<template>
  <div
    ref="observerElement"
    class="player-container"
  >
    <video
      ref="videoElement"
      class="video-js vjs-fill"
      preload="auto"
      playsinline
      muted
    >
      <source
        :src="source"
        type="application/x-mpegURL"
      >
    </video>
    <div
      v-if="showError"
      class="video-error"
    >
      <img
        src="@/assets/images/icons/no-cctv.png"
        alt="Error image"
        class="error-image"
      >
      <div class="error-message">
        {{ errorMessage }}
      </div>
    </div>
  </div>
</template>

<script>
// eslint-disable-next-line import/no-extraneous-dependencies
import videojs from 'video.js'
// eslint-disable-next-line import/no-extraneous-dependencies
import 'video.js/dist/video-js.css'

export default {
  props: {
    source: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      player: null,
      showError: false,
      errorMessage: '',
      retryTimeout: null,
      retryAttempted: false,
      retryCount: 0,
      maxRetries: 3,
    }
  },

  watch: {
    source: {
      handler(newSource) {
        if (this.player && newSource) {
          this.resetPlayer()
          this.loadSource(newSource)
        }
      },
    },
  },

  mounted() {
    this.initializeRtspPlayer()
  },

  beforeDestroy() {
    this.cleanupPlayer()
  },

  methods: {
    initializeRtspPlayer() {
      try {
        const options = {
          autoplay: true,
          fill: true,
          responsive: true,
          fluid: true,
          controls: false, // ปิด controls
          preload: 'auto',
          html5: {
            hls: {
              enableLowInitialPlaylist: true,
              smoothQualityChange: true,
              overrideNative: true,
              maxBufferLength: 30,
              maxBufferSize: 60 * 1000 * 1000,
            },
            nativeVideoTracks: false,
            nativeAudioTracks: false,
            nativeTextTracks: false,
          },
        }

        this.player = videojs(this.$refs.videoElement, options, () => {
          this.setupEventListeners()
          this.loadSource(this.source)
        })
      } catch (error) {
        console.error('Error initializing player:', error)
        this.handleError('Failed to initialize video player')
      }
    },

    setupEventListeners() {
      if (!this.player) return

      this.player.on('loadedmetadata', () => {
        this.showError = false
        this.errorMessage = ''
        this.player.play().catch(err => {
          console.error('Autoplay after metadata failed:', err)
        })
      })

      this.player.on('error', () => {
        this.handleStreamError()
      })

      this.player.on('playing', () => {
        this.showError = false
      })
    },

    loadSource(source) {
      if (!this.player) return

      try {
        this.player.src({
          src: source,
          type: 'application/x-mpegURL',
        })
        this.player.load()

        this.player.play().catch(err => {
          console.error('Play failed after loading source:', err)
          this.handleError('Failed to play video stream')
        })
      } catch (error) {
        console.error('Error loading source:', error)
        this.handleError('Failed to load video source')
      }
    },

    handleStreamError() {
      // eslint-disable-next-line no-plusplus
      this.retryCount++

      if (this.retryCount <= this.maxRetries) {
        this.showError = true
        this.errorMessage = `เชื่อมต่อไม่สำเร็จ กำลังลองใหม่... (${this.retryCount}/${this.maxRetries})`

        this.retryTimeout = setTimeout(() => {
          this.resetPlayer()
          this.loadSource(this.source)
        }, 5000)
      } else {
        this.showError = true
        this.errorMessage = 'ไม่สามารถเชื่อมต่อกับวิดีโอได้'
      }
    },

    handleError(message) {
      this.showError = true
      this.errorMessage = message
    },

    resetPlayer() {
      if (this.player) {
        this.player.reset()
        this.showError = false
        this.errorMessage = ''
        this.retryCount = 0
      }
    },

    cleanupPlayer() {
      if (this.retryTimeout) {
        clearTimeout(this.retryTimeout)
      }

      if (this.player) {
        this.player.dispose()
        this.player = null
      }
    },
  },
}
</script>

<style scoped>
.player-container {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 300px;
  background-color: black;
  overflow: hidden;
}

.video-js {
  width: 100% !important;
  height: 100% !important;
  position: absolute !important;
  top: 0;
  left: 0;
}

/* ซ่อน controls ทั้งหมด */
.video-js .vjs-control-bar,
.video-js .vjs-big-play-button,
.video-js .vjs-loading-spinner {
  display: none !important;
}

.video-error {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 2;
}

.error-image {
  max-width: 30%;
  height: auto;
  object-fit: contain;
  margin-bottom: 1rem;
}

.error-message {
  color: white;
  font-size: 1rem;
  text-align: center;
  padding: 1rem;
}
</style>
